/**
 * The rule behind the one fixed bottom slot (docs/design/MASTER.md section 5: on a phone, at most the navbar plus
 * one bottom element). Every fixed bottom element asks for the slot; the first one in this list that is asking gets
 * it, and the others draw nothing until it lets go.
 *
 * Kept free of React so the priority can be tested on its own (bottomSlotPriority.test.ts).
 */
export const BOTTOM_SLOT_PRIORITY = [
  // CompoundPage: the phone price bar, once the price block at the top has scrolled away.
  "compound-price",
  // BlogTOC: the floating "Contents" pill on database blog posts, below 1024px.
  "article-contents",
  // StickyBottomCTA: the site-wide next-step bar (quiz, Compare Prices, or the compound Buy bar on desktop).
  "sticky-cta",
] as const;

export type BottomSlotId = (typeof BOTTOM_SLOT_PRIORITY)[number];

/** The element that gets the slot: the highest priority one among those asking, or null when none is. */
export function pickBottomSlotOwner(asking: Iterable<BottomSlotId>): BottomSlotId | null {
  const set = new Set(asking);
  for (const id of BOTTOM_SLOT_PRIORITY) if (set.has(id)) return id;
  return null;
}

/**
 * The space to keep free at the end of the page for a fixed bottom element: its own height plus its distance from
 * the bottom edge (the Contents pill floats 16px up). Zero while it is not laid out (display: none at this width).
 */
export function reservedSpace(height: number, bottomOffset: number): number {
  if (!(height > 0)) return 0;
  return Math.ceil(height + Math.max(0, Number.isFinite(bottomOffset) ? bottomOffset : 0));
}
